import styled from 'styled-components'

import { Numeric, Tooltip } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import { useTranslation } from '@root/shared/localization'
import { Avatar, Truncate } from '@root/shared/ui-kit'

import type { WalletProfile } from '../model'

/**
 * Every token the wallet holds or has held, best first.
 *
 * A position whose buys reached us without a price gets a dash where its PnL
 * would be, and says why on hover. Drawing a zero there would be a lie the
 * eye cannot catch: it reads as "broke even" and it means "nobody recorded
 * what this cost".
 */

type Props = {
  positions: ReadonlyArray<WalletProfile.Position>
}

/**
 * Whether the full name says anything the ticker did not.
 *
 * Plenty of tokens are named after their ticker, and "Zecoisto Zecoisto" is
 * noise in the one column that is meant to tell them apart.
 */
const named = (position: WalletProfile.Position) =>
  position.name && position.name.trim() !== position.symbol.trim()

export const WalletProfilePositions: React.FC<Props> = ({ positions }) => {
  const { t } = useTranslation()

  return (
    <Root>
      <Head>
        <span>{t('wallet-profile.token')}</span>
        <Right>{t('position.bought')}</Right>
        <Right>{t('position.sold')}</Right>
        <Right>{t('common.holding')}</Right>
        <Right>{t('common.pnl')}</Right>
      </Head>
      {positions.map((position) => (
        <Row key={`${position.token}-${position.pairAddress}`}>
          <Token>
            <Avatar
              size={16}
              src={position.logo}
              alt={position.symbol || position.token}
            />
            <TokenName>
              {position.symbol || (
                <Truncate start={4} end={4}>
                  {position.token}
                </Truncate>
              )}
            </TokenName>
            {named(position) ? (
              <TokenFull>{position.name}</TokenFull>
            ) : undefined}
          </Token>
          <Right>
            <Numeric
              value={position.spentUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Right>
          <Right>
            <Numeric
              value={position.receivedUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Right>
          <Right>
            <Numeric
              value={position.remainingTokens * position.priceUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </Right>
          {position.costBasisKnown ? (
            <Right data-trend={Trend.of(position.pnl)}>
              <Numeric
                value={position.pnl}
                preset="currency-amount-short"
                currency="$"
              />
            </Right>
          ) : (
            <Right>
              <Tooltip content={t('wallet-profile.unpriced-note')}>
                <Unknown>{t('wallet-profile.unpriced')}</Unknown>
              </Tooltip>
            </Right>
          )}
        </Row>
      ))}
    </Root>
  )
}

const COLUMNS = `minmax(120px, 1fr) repeat(4, minmax(64px, 88px))`

const Root = styled.div`
  display: grid;
  gap: 2px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: ${COLUMNS};
  gap: 12px;
  align-items: center;

  padding: 5px 6px;
  border-radius: 6px;

  color: var(--text-10);
  font-size: 11px;
  font-variant-numeric: tabular-nums;

  &:hover {
    background: var(--appearanceA-1);
  }
`

/** Stays put while the rows scroll; a table without headings is a wall. */
const Head = styled(Row)`
  position: sticky;
  top: 0;
  z-index: 1;

  background: var(--background-1);
  color: var(--text-9);
  font-size: 10px;

  &:hover {
    background: var(--background-1);
  }
`

const Token = styled.div`
  overflow: hidden;
  display: flex;
  gap: 6px;
  align-items: center;

  color: var(--text-11);
`

const TokenName = styled.span`
  flex: none;
  font-weight: 500;
`

/** The full name, after the ticker, and the first thing to go when narrow. */
const TokenFull = styled.span`
  overflow: hidden;

  color: var(--text-9);
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Right = styled.div`
  text-align: right;

  &[data-trend='up'] {
    color: var(--up-9);
  }

  &[data-trend='down'] {
    color: var(--down-9);
  }
`

const Unknown = styled.span`
  color: var(--text-9);
  font-size: 10px;
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: help;
`
